import { useState } from "react";
import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useJsonLd } from "@/hooks/useJsonLd";

const faqs = [
  {
    q: "女神漫画角色图鉴收录哪些作品？",
    a: "图鉴按作品与设定两大维度分类，目前涵盖奇幻恋爱《星穹之下》、和风神话《月华祭典》、西幻圣域《极乐圣域》、都市奇幻《黄昏契约》以及海洋神话《苍蓝神殿》五大谱系。不同作品的设定风格差异较大，方便你按喜好浏览。",
  },
  {
    q: "角色的人气值是怎么来的？",
    a: "人气值综合了读者喜爱度、讨论热度与角色出场的重要性等多方面因素，是一个便于快速对比的相对参考值，并非绝对标准。你也可以在角色图鉴页按人气或名称排序查看。",
  },
  {
    q: "如何快速找到我感兴趣的角色？",
    a: "进入「角色图鉴」页后，你可以通过顶部的作品分类标签进行筛选，也可以使用搜索框按角色名、称号或设定标签进行检索。每种作品下的角色会同步展示，方便逐一浏览。",
  },
  {
    q: "图鉴内容会持续更新吗？",
    a: "会的。随着新作的连载与高人气女神角色的涌现，我们会持续补充新角色档案与作品专题。订阅页脚的更新通知后，即可第一时间收到整理好的新内容。",
  },
  {
    q: "我可以投稿或推荐角色吗？",
    a: "当然可以。如果你有特别喜欢的女神角色或作品想被收录，欢迎通过「关于我们」页面的联系方式发给我们。我们会认真评估并纳入后续整理计划。",
  },
];

export default function Faq() {
  usePageMeta(
    "常见问题｜女神漫画角色图鉴",
    "女神漫画角色图鉴常见问题解答：收录哪些作品、人气值如何计算、如何快速查找角色、是否持续更新以及如何投稿推荐角色等。"
  );

  const [open, setOpen] = useState<number>(0);

  useJsonLd(
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
    "faq"
  );

  return (
    <>
      <section className="relative flex min-h-[260px] md:min-h-[300px] w-full items-center overflow-hidden bg-foreground-950">
        <img
          src="https://readdy.ai/api/search-image?query=ethereal%20goddess%20seated%20among%20soft%20glowing%20question%20marks%20and%20stars%2C%20wide%20fantasy%20anime%20banner%2C%20warm%20amber%20crimson%20and%20ivory%20palette%2C%20luminous%20gentle%20atmosphere&width=1600&height=600&seq=faq-banner-2026&orientation=landscape"
          alt="常见问题横幅背景"
          className="absolute inset-0 h-full w-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/45 to-black/70"></div>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 pt-16 pb-8 text-white md:pt-24">
          <nav aria-label="面包屑" className="text-xs text-white/70">
            <span className="hover:text-white"><a href="/" rel="nofollow">首页</a></span>
            <span className="mx-2">/</span>
            <span>常见问题</span>
          </nav>
          <h1 className="mt-4 font-heading text-3xl font-black md:text-5xl">常见问题</h1>
          <p className="mt-4 max-w-2xl text-sm text-white/80 md:text-base">
            关于女神漫画角色图鉴，你可能想知道的都在这里。
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="mx-auto max-w-3xl px-4 md:px-6">
          <div className="space-y-4">
            {faqs.map((f, i) => {
              const isOpen = open === i;
              return (
                <div key={f.q} className="overflow-hidden rounded-lg border border-background-200 bg-background-100">
                  <button
                    type="button"
                    onClick={() => setOpen(isOpen ? -1 : i)}
                    aria-expanded={isOpen}
                    className="flex w-full items-center justify-between gap-4 px-5 py-4 text-left"
                  >
                    <span className="font-heading text-base font-bold text-foreground-900">
                      <span className="mr-2 text-primary-600">Q{i + 1}</span>
                      {f.q}
                    </span>
                    <i
                      className={`ri-add-line text-xl text-primary-600 transition-transform duration-300 ${isOpen ? "rotate-45" : ""}`}
                      aria-hidden="true"
                    ></i>
                  </button>
                  <div className={`grid transition-all duration-300 ${isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"}`}>
                    <div className="overflow-hidden">
                      <p className="px-5 pb-5 text-sm leading-relaxed text-foreground-600">{f.a}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-10 rounded-lg border border-primary-200 bg-primary-50 p-6 text-center">
            <p className="font-heading text-base font-bold text-primary-800">还有别的问题？</p>
            <p className="mt-1.5 text-sm text-primary-700">欢迎通过关于我们页面的联系方式与我们交流。</p>
            <Link
              to="/about"
              className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-primary-500 px-6 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap hover:bg-primary-600"
            >
              联系我们
              <i className="ri-arrow-right-line" aria-hidden="true"></i>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}